import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ASSET_STATUSES, AssetStatus } from '../../lib/types';
import { statusClass } from '../ui/Chip';
import { Icon } from '../ui/Icon';

interface Props<T extends string> {
  value: T;
  onChange: (next: T) => void;
  /** Status values offered in the dropdown. Defaults to the asset
   *  statuses; pass TASK_STATUSES to reuse the picker on task rows. */
  options?: readonly T[];
  /** When true, all click events stop propagation so the picker can sit
   *  inside a clickable parent row without triggering its handler. */
  stopParent?: boolean;
}

export const AssetStatusPicker = <T extends string = AssetStatus>({ value, onChange, options, stopParent }: Props<T>) => {
  const choices = options ?? (ASSET_STATUSES as unknown as readonly T[]);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (
        !triggerRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const stop = (e: React.MouseEvent) => {
    if (stopParent) e.stopPropagation();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`chip ${statusClass(value)}`}
        onClick={(e) => {
          stop(e);
          setOpen((v) => !v);
        }}
        title="Change status"
        style={{
          border: 'none',
          cursor: 'pointer',
          gap: 5,
        }}
      >
        <span className="dot" style={{ background: 'currentColor' }} />
        {value}
        <Icon name="chevronDown" size={10} className="icon" />
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          role="menu"
          onClick={stop}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            zIndex: 1000,
            padding: 4,
            minWidth: 160,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {choices.map((s) => (
            <button
              key={s}
              type="button"
              role="menuitem"
              onClick={(e) => {
                stop(e);
                if (s !== value) onChange(s);
                setOpen(false);
              }}
              className={`chip ${statusClass(s)}`}
              style={{
                justifyContent: 'flex-start',
                cursor: 'pointer',
                border: s === value ? '1px solid currentColor' : '1px solid transparent',
                fontWeight: s === value ? 700 : 500,
                padding: '4px 8px',
                fontSize: 12,
              }}
            >
              <span className="dot" style={{ background: 'currentColor' }} />
              {s}
              {s === value && (
                <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                  <Icon name="check" size={11} />
                </span>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};

export default AssetStatusPicker;
